<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import RentalUnitData = App.Data.EstateObject.RentalUnitData

const props = defineProps<{
  rentalUnits: RentalUnitData[]
}>()

const { n, t } = useI18n()

const emphasisClass = 'bg-surface-100! text-surface-950! font-semibold'
const rightClass = 'text-right!'
const rightBodyClass = rightClass
const rightEmphasisClass = `${emphasisClass} ${rightClass}`
const rowsPerPage = 15
const shouldPaginate = computed(() => props.rentalUnits.length > rowsPerPage)

const totals = computed(() => {
  const totalSpace = Math.round(props.rentalUnits.reduce((sum, unit) => sum + unit.space!, 0) * 100) / 100
  const totalNetRent = Math.round(props.rentalUnits.reduce((sum, unit) => sum + unit.net_rent, 0) * 100) / 100

  return {
    totalSpace,
    totalNetRent,
    rentPerSquareMeter: totalSpace > 0 ? Math.round((totalNetRent / totalSpace) * 100) / 100 : null,
  }
})

function rentPerSquareMeter(unit: RentalUnitData) {
  const space = unit.space!

  if (space <= 0) {
    return null
  }

  return unit.net_rent / space
}
</script>

<template>
  <DataTable
    :value="rentalUnits"
    :paginator="shouldPaginate"
    :rows="rowsPerPage"
    responsive-layout="scroll"
    class="border-surface-200! overflow-hidden rounded-md border">
    <ColumnGroup type="footer">
      <Row>
        <Column :footer="t('rental_units.total')" :colspan="2" :footer-class="emphasisClass" />
        <Column :footer="`${n(totals.totalSpace, 'decimal')} m²`" :footer-class="rightEmphasisClass" />
        <Column :footer="n(totals.totalNetRent, 'euros')" :footer-class="rightEmphasisClass" />
        <Column
          :footer="totals.rentPerSquareMeter === null ? '-' : n(totals.rentPerSquareMeter, 'euros')"
          :footer-class="rightEmphasisClass" />
        <Column footer="" :footer-class="emphasisClass" />
      </Row>
    </ColumnGroup>

    <Column :header="t('rental_units.unit')" :header-class="emphasisClass">
      <template #body="{ index }">
        {{ index + 1 }}
      </template>
    </Column>

    <Column field="type" :header="t('rental_units.type')" :header-class="emphasisClass">
      <template #body="{ data }: { data: RentalUnitData }">
        <Tag
          :value="data.type"
          severity="secondary"
          class="border-surface-200! bg-surface-0! text-surface-950! border" />
      </template>
    </Column>

    <Column
      field="space"
      :header="t('rental_units.space')"
      :header-class="rightEmphasisClass"
      :body-class="rightBodyClass">
      <template #body="{ data }: { data: RentalUnitData }">
        {{ `${n(data.space!, 'decimal')} m²` }}
      </template>
    </Column>

    <Column
      field="net_rent"
      :header="t('rental_units.net_rent')"
      :header-class="rightEmphasisClass"
      :body-class="rightBodyClass">
      <template #body="{ data }: { data: RentalUnitData }">
        {{ n(data.net_rent, 'euros') }}
      </template>
    </Column>

    <Column
      :header="t('rental_units.rent_per_square_meter')"
      :header-class="rightEmphasisClass"
      :body-class="rightBodyClass">
      <template #body="{ data }: { data: RentalUnitData }">
        <span v-if="rentPerSquareMeter(data) !== null">
          {{ n(rentPerSquareMeter(data) ?? 0, 'euros') }}
        </span>
        <span v-else>-</span>
      </template>
    </Column>

    <Column field="is_vacant" :header="t('rental_units.status')" :header-class="emphasisClass">
      <template #body="{ data }: { data: RentalUnitData }">
        <Tag
          :value="data.is_vacant ? t('rental_units.vacant') : t('rental_units.rented')"
          :severity="data.is_vacant ? 'danger' : 'success'" />
      </template>
    </Column>

    <template #empty>
      <div class="text-surface-500 py-6 text-center">
        {{ t('rental_units.empty') }}
      </div>
    </template>
  </DataTable>
</template>

<i18n>
{
  "de": {
    "rental_units": {
      "unit": "Einheit",
      "type": "Typ",
      "space": "Fläche",
      "net_rent": "Kaltmiete",
      "rent_per_square_meter": "€/m²",
      "status": "Status",
      "total": "Summe / Durchschnitt",
      "rented": "Vermietet",
      "vacant": "Leerstand",
      "empty": "Keine Einheiten vorhanden"
    },
    "unit_types": {
      "living": "Wohnung",
      "commercial": "Gewerbe",
      "parking": "Parkplatz",
      "garage": "Garage",
      "other": "Sonstiges"
    }
  },
  "en": {
    "rental_units": {
      "unit": "Unit",
      "type": "Type",
      "space": "Area",
      "net_rent": "Net rent",
      "rent_per_square_meter": "€/m²",
      "status": "Status",
      "total": "Total / Average",
      "rented": "Rented",
      "vacant": "Vacant",
      "empty": "No units available"
    },
    "unit_types": {
      "living": "Apartment",
      "commercial": "Commercial",
      "parking": "Parking",
      "garage": "Garage",
      "other": "Other"
    }
  }
}
</i18n>
